import * as React from 'react';

import { DEFAULT_LIMIT } from '../data-table/cursor-search';

import {
  filterParamKeys,
  normalizeFilterValues,
  parseFilterValues,
  serializeFilterValues,
} from './filter-codec';
import { listParams, type ListParams, type SearchParamValue } from './index-search';
import { nonEmpty } from './list-state';
import type { CursorListState, FilterDef, ListQuery } from './types';

/** A search patch: `undefined` removes the param from the URL. */
export type SearchPatch = Record<string, SearchParamValue | undefined>;

export interface UseSearchListStateOptions {
  /**
   * The route's validated search params (`Route.useSearch()`), usually from
   * `indexSearchSchema`. Filters read their own flat params from it:
   * `status=active,draft`, `total_gte=1000`.
   */
  search: Readonly<Record<string, unknown>>;
  /**
   * Merges a patch into the URL, usually
   * `(patch) => navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })`.
   */
  onSearchChange: (patch: SearchPatch) => void;
  /** The table's filters: each one reads and writes its own flat params. */
  filters?: ReadonlyArray<FilterDef<never>>;
  /** The endpoint's page size when `limit` is absent. Default 20, the backend's. */
  defaultLimit?: number;
}

export type SearchListState = CursorListState & {
  /** The query as API params (`q`, `sort`, `status`, `total_gte`, `cursor`, `limit`), ready for a fetch. */
  params: ListParams;
};

/**
 * `"10"` -> `10`, `"true"` -> `true`: what the router would parse the param
 * into anyway. Written as a string, it would come back quoted (`%2210%22`).
 */
function toSearchValue(value: string): SearchParamValue {
  if (value === 'true' || value === 'false') return value === 'true';
  const number = Number(value);
  return value.trim() !== '' && String(number) === value ? number : value;
}

/**
 * List state in the URL, owned by the route's `validateSearch` (ADR 17):
 * `?q=ring&status=active,draft&total_gte=1000&sort=-placedAt&cursor=...`.
 * The route declares the params (`indexSearchSchema`); this hook maps them to
 * and from the table's list state. Every search, filter or sort change drops
 * the cursor, so the list starts again at its first page.
 */
export function useSearchListState({
  search,
  onSearchChange,
  filters: defs = [],
  defaultLimit = DEFAULT_LIMIT,
}: UseSearchListStateOptions): SearchListState {
  const filterKeys = React.useMemo(() => defs.flatMap((def) => filterParamKeys(def)), [defs]);

  const q = nonEmpty(typeof search.q === 'string' ? search.q : undefined);
  const sort = nonEmpty(typeof search.sort === 'string' ? search.sort : undefined);
  const cursor = nonEmpty(typeof search.cursor === 'string' ? search.cursor : undefined);
  const limit = typeof search.limit === 'number' ? search.limit : defaultLimit;

  // Re-parse only when one of this table's filter params changes, so
  // `filters` keeps its identity across cursor and sort changes.
  const filterParams = filterKeys
    .map((name) => {
      const raw = search[name];
      const value =
        typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
          ? String(raw)
          : '';
      return `${name}=${encodeURIComponent(value)}`;
    })
    .join('&');
  const filters = React.useMemo(
    () => parseFilterValues(defs, Object.fromEntries(new URLSearchParams(filterParams))),
    [defs, filterParams],
  );

  const update = React.useCallback(
    (patch: Partial<ListQuery>) => {
      const next: SearchPatch = { cursor: undefined };
      if ('q' in patch) next.q = nonEmpty(patch.q);
      if ('sort' in patch) next.sort = nonEmpty(patch.sort);
      if (patch.filters) {
        const serialized = serializeFilterValues(defs, normalizeFilterValues(patch.filters));
        for (const [name, value] of Object.entries(serialized)) {
          next[name] = value === null ? undefined : toSearchValue(value);
        }
      }
      onSearchChange(next);
    },
    [defs, onSearchChange],
  );

  const setCursor = React.useCallback(
    (nextCursor: string | undefined) => onSearchChange({ cursor: nonEmpty(nextCursor) }),
    [onSearchChange],
  );

  const setLimit = React.useCallback(
    (nextLimit: number) =>
      onSearchChange({
        cursor: undefined,
        limit: nextLimit === defaultLimit ? undefined : nextLimit,
      }),
    [defaultLimit, onSearchChange],
  );

  // Same function as the route loader's prefetch, so both share a query key.
  const limitParam = typeof search.limit === 'number' ? search.limit : undefined;
  const params = React.useMemo(
    () =>
      listParams(
        {
          ...Object.fromEntries(new URLSearchParams(filterParams)),
          q,
          sort,
          cursor,
          limit: limitParam,
        },
        defs,
      ),
    [cursor, defs, filterParams, limitParam, q, sort],
  );

  return React.useMemo(
    () => ({ q, sort, filters, update, cursor, limit, setCursor, setLimit, params }),
    [cursor, filters, limit, params, q, setCursor, setLimit, sort, update],
  );
}
